import { Id } from "@dgs/core";
import { DataFieldType } from "../../../shared/types/dataField";
import { DataFieldOptionIndexResource } from "../../../shared/types/dataFieldOption";
import { TenantOptionResource } from "../../../shared/types/tenant";
import {
	ContactAttributeOption,
	ContactFilterFormValues,
	ContactFilterItemFormValues,
	ContactFilterOperator,
	ContactFilterSaveRequest,
	ContactFilterUpdateResource,
	ContactRelationColumn,
	CountryOptionResource,
	EventOptionResource,
	ICompanyOptionResource,
	IContactAttributeResource,
	IContactFilterItemRequest,
	IContactFilterItemResource,
	IDistributionListOption,
	TagIndexResource,
	contactRelationEntityTypeToColumns,
} from "../shared/types";
import { ContactTypeOptionResource } from "../shared/types/contactType";
import { BetweenValue } from "./types";

const mapFilterItemResourceToFormValues = (filterItem: IContactFilterItemResource): ContactFilterItemFormValues => ({
	value: filterItem.value,
	contactAttributeId: filterItem.contactAttribute.id,
	operator: filterItem.operator,
	relationColumn: filterItem.relationColumn,
});

export const mapResourceToFormValues = (contactFilter: ContactFilterUpdateResource): ContactFilterFormValues => ({
	translations: contactFilter.translations,
	type: contactFilter.type,
	filterItems: contactFilter.items.map(mapFilterItemResourceToFormValues),
	showInSidebar: contactFilter.showInSidebar,
});

const pickId = (value: unknown) => {
	if (Array.isArray(value)) {
		return value.map((v) => (v as { id: Id }).id);
	} else {
		return (value as { id: Id }).id;
	}
};
const mapFilterItemFormValuesToRequest = (
	formValues: ContactFilterItemFormValues,
	options: ContactAttributeOption[],
): IContactFilterItemRequest | null => {
	const option = options.find(({ id }) => id === formValues.contactAttributeId);
	if (!option) return null;

	let value = formValues.value;
	if (value !== null) {
		switch (option.attribute.type) {
			case "relation":
				switch (option.attribute.entityType) {
					case "addresses":
					case "nonPrimaryAddresses":
					case "primaryAddress":
						switch (formValues.relationColumn) {
							case "countryId": {
								value = pickId(value);
								break;
							}
						}
						break;
					case "companies":
					case "primaryCompany":
					case "nonPrimaryCompanies":
					case "tenants":
					case "distributionLists":
					case "contactType":
					case "invitationLists":
					case "tags": {
						value = pickId(value);
						break;
					}
					case "applications":
						break;
				}
				break;
			case "dataField": {
				const dataField = option.dataField;
				if (!dataField) break;

				switch (dataField.type) {
					case DataFieldType.MULTI_SELECT_FIELD:
					case DataFieldType.SELECT_FIELD:
					case DataFieldType.RADIO:
					case DataFieldType.CHECKBOX_GROUP:
						value = pickId(value);
						break;
				}

				break;
			}
		}
	}

	return { ...formValues, value, operator: formValues.operator as ContactFilterOperator };
};

export const mapFormValuesToRequest = (
	formValues: ContactFilterFormValues,
	options: ContactAttributeOption[],
): ContactFilterSaveRequest => ({
	translations: formValues.translations,
	type: formValues.type,
	showInSidebar: formValues.showInSidebar,
	filterItems: formValues.filterItems.flatMap((item) => {
		const mapped = mapFilterItemFormValuesToRequest(item, options);
		return mapped ? [mapped] : [];
	}),
});

const singleRelationColumnOrNull = (attribute: IContactAttributeResource): ContactRelationColumn | null => {
	if (attribute.type !== "relation") return null;

	const availableRelationColumns = contactRelationEntityTypeToColumns[attribute.entityType];

	if (availableRelationColumns.length === 1) {
		return availableRelationColumns[0];
	} else {
		return null;
	}
};

export const maToEmptyFilterItemFormValues = (attribute: IContactAttributeResource): ContactFilterItemFormValues => ({
	contactAttributeId: attribute.id,
	value: null,
	operator: null,
	relationColumn: singleRelationColumnOrNull(attribute),
});

const pickPropertyValue =
	<T, K extends keyof T>(propName: K) =>
	(value: unknown) => {
		if (typeof value === "object" && value != null && propName in value) {
			return String((value as T)[propName]);
		} else {
			return "Unknown";
		}
	};

const formatBetweenValue =
	(formatValue: (value: string) => string) =>
	(value: unknown): string => {
		const start = formatValue(pickPropertyValue<BetweenValue, "start">("start")(value));
		const end = formatValue(pickPropertyValue<BetweenValue, "end">("end")(value));

		return `${start} - ${end}`;
	};

export const mapFilterItemValueToString = (
	option: ContactAttributeOption,
	item: IContactFilterItemResource,
	{
		formatDate,
		formatDateTime,
		t,
	}: {
		formatDate: (value: string) => string;
		formatDateTime: (value: string) => string;
		t: (value: string) => string;
	},
): string => {
	if (item.value == null) return "";

	const attribute = option.attribute;
	const asArray = Array.isArray(item.value) ? item.value : [item.value];

	switch (attribute.type) {
		case "column":
			return asArray.map(String).join(", ");
		case "dataField":
			if (!option.dataField) return "Unknown";
			switch (option.dataField.type) {
				case DataFieldType.MULTI_SELECT_FIELD:
				case DataFieldType.SELECT_FIELD:
				case DataFieldType.CHECKBOX_GROUP:
				case DataFieldType.RADIO:
					return asArray.map(pickPropertyValue<DataFieldOptionIndexResource, "value">("value")).join(", ");
				case DataFieldType.CHECKBOX:
					return asArray.map((value: boolean) => (value ? t("Yes") : t("No"))).join(", ");
				case DataFieldType.EMAIL:
				case DataFieldType.LONG_TEXT:
				case DataFieldType.URL:
				case DataFieldType.NUMBER:
				case DataFieldType.TEXT:
				case DataFieldType.PHONE:
				case DataFieldType.TIME:
					return asArray.map(String).join(", ");
				case DataFieldType.DATE: {
					if (item.operator === "BETWEEN") {
						return asArray.map(formatBetweenValue(formatDate)).join(", ");
					} else {
						return asArray.map(formatDate).join(", ");
					}
				}
				case DataFieldType.DATETIME: {
					if (item.operator === "BETWEEN") {
						return asArray.map(formatBetweenValue(formatDateTime)).join(", ");
					} else {
						return asArray.map(formatDateTime).join(", ");
					}
				}
				default:
					return "Unknown";
			}
		case "relation":
			switch (attribute.entityType) {
				case "addresses":
				case "primaryAddress":
				case "nonPrimaryAddresses":
					switch (item.relationColumn) {
						case "zip":
							if (item.operator === "BETWEEN") {
								return asArray.map(formatBetweenValue(String)).join(", ");
							}
							return asArray.map(String).join(", ");
						case "countryId": {
							return asArray.map(pickPropertyValue<CountryOptionResource, "name">("name")).join(", ");
						}
						default:
							return asArray.map(String).join(", ");
					}
				case "companies":
				case "primaryCompany":
				case "nonPrimaryCompanies":
					return asArray.map(pickPropertyValue<ICompanyOptionResource, "name">("name")).join(", ");
				case "emails":
				case "primaryEmail":
				case "nonPrimaryEmails":
					return asArray.map(String).join(", ");
				case "phones":
				case "primaryPhone":
				case "nonPrimaryPhones":
					return asArray.map(String).join(", ");
				case "tenants":
					return asArray.map(pickPropertyValue<TenantOptionResource, "name">("name")).join(", ");
				case "tags":
					return asArray.map(pickPropertyValue<TagIndexResource, "name">("name")).join(", ");
				case "distributionLists":
					return asArray.map(pickPropertyValue<IDistributionListOption, "name">("name")).join(", ");
				case "invitationLists":
					return asArray.map(pickPropertyValue<EventOptionResource, "name">("name")).join(", ");
				case "applications":
					return asArray.map((v) => t(v)).join(", ");
				case "contactType":
					return asArray.map(pickPropertyValue<ContactTypeOptionResource, "name">("name")).join(", ");
			}
	}
};
